// ONE continuous dust field behind the hero + TrustedServicesFlow sections.
// Paints each frame: #070808 backdrop → hero radial glow → trusted bloom →
// ambient dust (full page, with downward envelope) → pyramid particles (hero
// only, 15s convergence cycle) → crisp mark overlay at full convergence.
// Ported verbatim from the design prototype's page-field.jsx.

import React, { useRef, useEffect } from "react";

function ss(e0, e1, x) {
  const t = Math.max(0, Math.min(1, (x - e0) / (e1 - e0)));
  return t * t * (3 - 2 * t);
}
function s01(t) {
  const u = Math.max(0, Math.min(1, t));
  return u * u * (3 - 2 * u);
}
function lerp(a, b, t) { return a + (b - a) * t; }

function PageField({ w, heroH, flowH }) {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;

    const totalH = heroH + flowH;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width  = Math.round(w * dpr);
    canvas.height = Math.round(totalH * dpr);
    const ctx = canvas.getContext("2d");
    ctx.scale(dpr, dpr);

    const reduce = window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    // ── Ambient dust — seeded over the ENTIRE page height ───────────────
    const AMB = Math.round((w * totalH) / 520);
    const amb = [];
    for (let i = 0; i < AMB; i++) {
      amb.push({
        sx:    Math.random() * w,
        sy:    Math.random() * totalH,
        phase: Math.random() * 6.2832,
        amp:   3 + Math.random() * 13,
        speed: 0.0003 + Math.random() * 0.0007,
        r:     0.55 + Math.random() * 0.95,
        baseA: 0.16 + Math.random() * 0.24,
        tw:    Math.random() * 6.2832,
      });
    }

    // Density envelope: full through hero + Trusted, fades to whisper by Services
    const fadeStart = heroH + 380;
    const fadeEnd   = heroH + 780;
    const envelope = (y) => {
      if (y < fadeStart) return 1;
      if (y < fadeEnd)   return 1 - s01((y - fadeStart) / (fadeEnd - fadeStart)) * 0.93;
      return 0.07;
    };

    // ── Hero converging particles — seeded in upper hero only ────────────
    let heroP = [], markImg = null;
    let mpw = 0, mph = 0, mox = 0, moy = 0;

    const img = new Image();
    img.onload = () => {
      const pw = Math.round(Math.min(360, w * (w < 1024 ? 0.345 : 0.30)));
      const ph = Math.round(pw * img.height / img.width);

      const off = document.createElement("canvas");
      off.width = pw; off.height = ph;
      const octx = off.getContext("2d");
      octx.drawImage(img, 0, 0, pw, ph);
      const data = octx.getImageData(0, 0, pw, ph).data;

      const pts = [];
      for (let py = 0; py < ph; py++) {
        for (let px = 0; px < pw; px++) {
          if (data[(py * pw + px) * 4 + 3] > 110) pts.push([px, py]);
        }
      }

      const stride = Math.max(1, Math.floor(pts.length / 4000));
      const chosen = [];
      for (let i = 0; i < pts.length; i += stride) chosen.push(pts[i]);
      // Fisher-Yates shuffle
      for (let i = chosen.length - 1; i > 0; i--) {
        const j = (Math.random() * (i + 1)) | 0;
        const tmp = chosen[i]; chosen[i] = chosen[j]; chosen[j] = tmp;
      }

      const ox = Math.round(w / 2 - pw / 2);
      const oy = Math.round(heroH * 0.34 - ph / 2);
      markImg = img; mpw = pw; mph = ph; mox = ox; moy = oy;

      heroP = chosen.map(([px, py]) => ({
        sx:    Math.random() * w,
        sy:    Math.random() * heroH * 0.72,
        phase: Math.random() * 6.2832,
        amp:   4 + Math.random() * 16,
        speed: 0.0003 + Math.random() * 0.0007,
        r:     0.5 + Math.random() * 0.9,
        baseA: 0.16 + Math.random() * 0.24,
        tw:    Math.random() * 6.2832,
        tx:    ox + px,
        ty:    oy + py,
      }));
    };
    img.src = "/grassurn-icon.png";

    const CYCLE = 15000;
    const start = performance.now();

    function draw(now) {
      const t = ((now - start) % CYCLE) / CYCLE;
      let form = 0;
      if      (t < 0.20) form = ss(0, 0.20, t);
      else if (t < 0.68) form = 1;
      else if (t < 0.82) form = 1 - ss(0.68, 0.82, t);
      // else form = 0 (scattered)

      // 1. Backdrop
      ctx.fillStyle = "#070808";
      ctx.fillRect(0, 0, w, totalH);

      // 2. Hero radial glow — brightens slightly as mark converges
      let g = ctx.createRadialGradient(w / 2, heroH * 0.34, 0, w / 2, heroH * 0.34, heroH * 0.9);
      g.addColorStop(0, `rgba(91,148,144,${0.13 + form * 0.05})`);
      g.addColorStop(1, "rgba(91,148,144,0)");
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, w, totalH);

      // 3. Soft bloom behind Trusted headline
      const by = heroH + 250;
      const g2 = ctx.createRadialGradient(w / 2, by, 0, w / 2, by, 560);
      g2.addColorStop(0, "rgba(91,148,144,0.10)");
      g2.addColorStop(1, "rgba(91,148,144,0)");
      ctx.fillStyle = g2;
      ctx.fillRect(0, 0, w, totalH);

      const imgAlpha = ss(0.62, 1.0, form);

      // 4. Ambient dust (full page, enveloped)
      for (let i = 0; i < amb.length; i++) {
        const p = amb[i];
        const x  = p.sx + Math.sin(now * p.speed + p.phase) * p.amp;
        const y  = p.sy + Math.cos(now * p.speed * 0.8 + p.phase) * p.amp;
        const tw = 0.65 + 0.35 * Math.sin(now * 0.0009 + p.tw);
        const a  = p.baseA * tw * envelope(p.sy);
        if (a <= 0.004) continue;
        ctx.beginPath();
        ctx.arc(x, y, p.r, 0, 6.2832);
        ctx.fillStyle = `rgba(162,190,188,${a})`;
        ctx.fill();
      }

      // 5. Hero pyramid particles (upper hero only, converge per cycle)
      for (let i = 0; i < heroP.length; i++) {
        const p  = heroP[i];
        const ax = p.sx + Math.sin(now * p.speed + p.phase) * p.amp;
        const ay = p.sy + Math.cos(now * p.speed * 0.8 + p.phase) * p.amp;
        const shimmer = form > 0.9 ? Math.sin(now * 0.002 + p.phase) * 0.5 : 0;
        const x  = lerp(ax, p.tx + shimmer, form);
        const y  = lerp(ay, p.ty + shimmer, form);
        const tw = 0.65 + 0.35 * Math.sin(now * 0.0009 + p.tw);
        const a  = lerp(p.baseA * tw, 0.85, form) * (1 - imgAlpha * 0.5);
        ctx.beginPath();
        ctx.arc(x, y, p.r, 0, 6.2832);
        ctx.fillStyle = form > 0.5
          ? `rgba(123,181,177,${a})`
          : `rgba(162,190,188,${a})`;
        ctx.fill();
      }

      // 6. Crisp mark overlay — fades in near full convergence
      if (markImg && imgAlpha > 0.001) {
        ctx.imageSmoothingEnabled  = true;
        ctx.imageSmoothingQuality  = "high";
        const cx = mox + mpw / 2;
        const cy = moy + mph / 2;
        const gg = ctx.createRadialGradient(cx, cy, 0, cx, cy, mpw * 0.72);
        gg.addColorStop(0, `rgba(123,181,177,${0.14 * imgAlpha})`);
        gg.addColorStop(1, "rgba(123,181,177,0)");
        ctx.fillStyle = gg;
        ctx.fillRect(0, 0, w, totalH);
        ctx.globalAlpha = imgAlpha;
        ctx.drawImage(markImg, mox, moy, mpw, mph);
        ctx.globalAlpha = 1;
      }
    }

    let raf, running = true;

    if (reduce) {
      const id = setTimeout(() => draw(start + CYCLE * 0.4), 80);
      return () => clearTimeout(id);
    }

    const frame = (now) => {
      if (!running) return;
      draw(now);
      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);
    return () => { running = false; cancelAnimationFrame(raf); };
  }, [w, heroH, flowH]);

  return (
    <canvas
      ref={ref}
      aria-hidden="true"
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: w,
        height: heroH + flowH,
        display: "block",
        pointerEvents: "none",
        zIndex: 0,
      }}
    />
  );
}

export default PageField;
